import type { HrSample, HrZone, HrZoneId } from '@/lib/heartRateZones';
import { classifyBpm, normalizeHrTimestamp } from '@/lib/heartRateZones';

export const HR_ZONE_STROKE_COLORS: Record<HrZoneId, string> = {
  z1: '#60a5fa',
  z2: '#34d399',
  z3: '#fbbf24',
  z4: '#fb923c',
  z5: '#f87171',
};

export type RoutePointWithTime = { lat: number; lng: number; timestamp?: number };

export type ColoredRouteSegment = {
  points: [number, number][];
  color: string;
  zone: HrZoneId | null;
};

export type BuildZoneColoredRouteOptions = {
  /** Run start (ms) — used to assign timestamps to GPS points saved without timestamps. */
  runStartMs?: number;
  /** Active run duration in seconds — pairs with runStartMs. */
  runDurationSec?: number;
};

/** Linear HR interpolation at a GPS timestamp (ms). */
function interpolateBpmAtTime(samples: HrSample[], timestamp: number): number | null {
  if (samples.length === 0) return null;
  const ts = normalizeHrTimestamp(timestamp);
  const sorted = [...samples].sort((a, b) => a.timestamp - b.timestamp);

  if (ts <= sorted[0].timestamp) return sorted[0].bpm;
  if (ts >= sorted[sorted.length - 1].timestamp) return sorted[sorted.length - 1].bpm;

  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (ts >= a.timestamp && ts <= b.timestamp) {
      const span = b.timestamp - a.timestamp;
      if (span <= 0) return a.bpm;
      const t = (ts - a.timestamp) / span;
      return a.bpm + t * (b.bpm - a.bpm);
    }
  }
  return sorted[sorted.length - 1].bpm;
}

function resolvePointTimestamp(
  point: RoutePointWithTime,
  index: number,
  routePoints: RoutePointWithTime[],
  hrSamples: HrSample[],
  options?: BuildZoneColoredRouteOptions,
): number {
  if (point.timestamp != null && Number.isFinite(point.timestamp)) {
    return normalizeHrTimestamp(point.timestamp);
  }
  if (
    options?.runStartMs != null &&
    options.runDurationSec != null &&
    options.runDurationSec > 0 &&
    routePoints.length > 1
  ) {
    const fraction = index / (routePoints.length - 1);
    return options.runStartMs + fraction * options.runDurationSec * 1000;
  }
  const sortedHr = [...hrSamples].sort((a, b) => a.timestamp - b.timestamp);
  const start = sortedHr[0].timestamp;
  const end = sortedHr[sortedHr.length - 1].timestamp;
  const fraction = index / Math.max(1, routePoints.length - 1);
  return start + fraction * (end - start);
}

/** Split GPS route into MapKit segments colored by heart-rate zone along the run timeline. */
export function buildZoneColoredRouteSegments(
  routePoints: RoutePointWithTime[],
  hrSamples: HrSample[],
  zones: HrZone[],
  options?: BuildZoneColoredRouteOptions,
): ColoredRouteSegment[] {
  if (routePoints.length < 2 || hrSamples.length === 0) return [];

  const out: ColoredRouteSegment[] = [];
  let currentZone: HrZoneId | null = null;
  let currentPoints: [number, number][] = [];

  const flush = () => {
    if (currentPoints.length >= 2) {
      out.push({
        points: [...currentPoints],
        color: currentZone ? HR_ZONE_STROKE_COLORS[currentZone] : '#0bd9a8',
        zone: currentZone,
      });
    }
    currentPoints = currentPoints.length > 0 ? [currentPoints[currentPoints.length - 1]] : [];
  };

  for (let i = 0; i < routePoints.length; i++) {
    const p = routePoints[i];
    const pt: [number, number] = [p.lat, p.lng];
    const ts = resolvePointTimestamp(p, i, routePoints, hrSamples, options);
    const bpm = interpolateBpmAtTime(hrSamples, ts);
    const zone = bpm != null ? classifyBpm(Math.round(bpm), zones) : null;

    if (i === 0) {
      currentZone = zone;
      currentPoints.push(pt);
      continue;
    }

    if (zone !== currentZone) {
      currentPoints.push(pt);
      flush();
      currentZone = zone;
      currentPoints.push(pt);
    } else {
      currentPoints.push(pt);
    }
  }

  flush();
  return out.filter((seg) => seg.points.length >= 2);
}
